import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, User, Circle } from "lucide-react";

export default function Chat() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadContacts = async () => {
    try { const { data } = await api.get("/chat/contacts"); setContacts(data);
      if (!selected && data.length > 0) setSelected(data[0]);
    } catch(e){ toast.error(formatErr(e)); }
  };

  const loadThread = async (uid) => {
    try { const { data } = await api.get("/chat/messages", { params: { with_user: uid } }); setMsgs(data); window.dispatchEvent(new Event("chat-read")); }
    catch(e){ toast.error(formatErr(e)); }
  };

  useEffect(() => { loadContacts(); const t = setInterval(loadContacts, 15000); return () => clearInterval(t); }, []); // eslint-disable-line
  useEffect(() => { if (selected) loadThread(selected.id); }, [selected?.id]); // eslint-disable-line
  useEffect(() => {
    if (!selected) return;
    const t = setInterval(() => loadThread(selected.id), 8000);
    return () => clearInterval(t);
  }, [selected?.id]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({top: scrollRef.current.scrollHeight, behavior: "smooth"}); }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/chat/messages", { to_user_id: selected.id, text });
      setText(""); loadThread(selected.id); loadContacts();
    } catch(e){ toast.error(formatErr(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="grid md:grid-cols-[280px,1fr] gap-4 h-[calc(100vh-8rem)]" data-testid="chat-page">
      {/* Contacts */}
      <aside className="border border-border rounded-md bg-card flex flex-col overflow-hidden">
        <div className="p-3 border-b border-border">
          <div className="flex items-center gap-2 font-heading font-bold">
            <MessageCircle className="w-5 h-5 text-primary" />
            {user.role === "super_admin" ? "جهات الاتصال" : "المدير العام"}
          </div>
        </div>
        <div className="flex-1 overflow-auto">
          {contacts.length === 0 && <div className="p-4 text-center text-sm text-muted-foreground">لا توجد جهات</div>}
          {contacts.map(c => (
            <button key={c.id} onClick={()=>setSelected(c)} data-testid={`contact-${c.id}`}
              className={`w-full text-start p-3 border-b border-border hover:bg-accent transition-colors ${selected?.id === c.id ? "bg-accent" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-xs font-bold shrink-0">
                    {c.name?.charAt(0) || "?"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{c.last_message || (c.role === "super_admin" ? "مدير عام" : "مدير شركة")}</div>
                  </div>
                </div>
                {c.unread > 0 && (
                  <span className="text-[10px] bg-primary text-primary-foreground w-5 h-5 rounded-full grid place-items-center font-bold" data-testid={`unread-${c.id}`}>{c.unread}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Thread */}
      <section className="border border-border rounded-md bg-card flex flex-col overflow-hidden">
        {selected ? (
          <>
            <div className="p-3 border-b border-border flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold">
                {selected.name?.charAt(0) || "?"}
              </div>
              <div>
                <div className="font-heading font-bold">{selected.name}</div>
                <div className="text-xs text-muted-foreground">{selected.email}</div>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-2">
              {msgs.length === 0 && <div className="text-center text-sm text-muted-foreground py-12">لا توجد رسائل — ابدأ المحادثة</div>}
              {msgs.map(m => {
                const mine = m.from_user_id === user.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-md text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                      <div className="whitespace-pre-wrap">{m.text}</div>
                      <div className={`text-[10px] mt-1 ${mine ? "opacity-70" : "text-muted-foreground"}`}>
                        {new Date(m.created_at).toLocaleString("ar-EG", {hour:"2-digit", minute:"2-digit", day:"2-digit", month:"2-digit"})}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
              <Input value={text} onChange={(e)=>setText(e.target.value)} placeholder="اكتب رسالتك..." data-testid="chat-input" className="h-10" disabled={sending} />
              <Button type="submit" disabled={sending || !text.trim()} className="h-10" data-testid="chat-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </>
        ) : (
          <div className="flex-1 grid place-items-center text-muted-foreground text-sm">
            اختر جهة اتصال لبدء المحادثة
          </div>
        )}
      </section>
    </div>
  );
}
